/**
 * A resident's building, or -- when eMerge has none -- their CounselEAR
 * Preferred Clinic, labelled as not confirmed (2026-10-14).
 *
 * Isaiah Cotto, 2026-10-14: "we need to ensure we are getting their
 * facility/clinic as well from CounselEAR; that cannot be missing and is super
 * important for scheduling." OPERATIONS RULING, April Torino, 2026-10-13: the
 * Preferred Clinic is the facility the resident lives at. Until a person
 * confirms which eMerge building that clinic is, the clinic's name is shown in
 * place of "not on file", and says it is not confirmed.
 *
 * The server sends `counselear_clinic_id` and `counselear_clinic_name` beside
 * `facility_name` (services/counselear/clinicNames.js). Nothing here decides a
 * building; it only words what the row already carries.
 */
import React from 'react';

export interface ClinicFields {
  facility_name?: string | null;
  counselear_clinic_id?: string | null;
  counselear_clinic_name?: string | null;
}

export const UNCONFIRMED = '(building not confirmed)';

/** The clinic phrase alone, or null when the row names no clinic. */
export function clinicWords(row: ClinicFields | null | undefined): string | null {
  if (!row) return null;
  if (row.counselear_clinic_name) return `CounselEAR clinic: ${row.counselear_clinic_name} ${UNCONFIRMED}`;
  if (row.counselear_clinic_id) return `CounselEAR clinic ${row.counselear_clinic_id} ${UNCONFIRMED}`;
  return null;
}

/** The words for a resident's building: the building, else the clinic (unconfirmed), else the fallback. */
export function buildingWords(row: ClinicFields | null | undefined, fallback = 'not on file'): string {
  if (row && row.facility_name) return row.facility_name;
  return clinicWords(row) || fallback;
}

const TITLE = 'eMerge has no confirmed building for this resident. This is their Preferred Clinic in CounselEAR, which is the facility they live at (Operations ruling, 2026-10-13). Manifest Review → No building confirms the clinic\'s building.';

/**
 * The building's name as plain text; else the clinic, in an element a reader
 * can tell apart (`building-unconfirmed`, with a title saying why); else the
 * caller's own fallback node (its existing "not on file" treatment).
 */
export default function BuildingOrClinic({ row, fallback, className }: {
  row: ClinicFields | null | undefined;
  fallback?: React.ReactNode;
  className?: string;
}) {
  if (row && row.facility_name) return <>{row.facility_name}</>;
  const words = clinicWords(row);
  if (words) {
    return (
      <span className={['building-unconfirmed', className].filter(Boolean).join(' ')} title={TITLE} data-testid="building-unconfirmed">
        {words}
      </span>
    );
  }
  return <>{fallback ?? 'not on file'}</>;
}
